/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Position, NodeChange, Handle } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { DeleteConfirmationModal } from "../../Shared/DeleteConfirmationModal";
import { ProjectContext } from "../../UserApp";
import { NodeSyntaxHighlighter } from "../Components/NodeSyntaxHighlighter";
import {
  findNodeHandleForEntry,
  isNodeTitleValid,
  nodeHasEdge,
  PARENT_HANDLE_ID,
  ReactFlowEdge,
  singleHandleHasEdge,
  TransformEngineNodeData,
  TransformEngineNodeEntry,
  TransformEngineNodeEntryId,
} from "./Util/TransformEngineDataUtils";
import { createHandleComponentsForHandle } from "./TransformEngineNodeComponent";
import { EXPANDED_HANDLE_CLASS } from "./handleHitArea";
import { BiFile } from "react-icons/bi";
import { Button } from "baseui/button";
import { Input } from "baseui/input";
import { FormControl } from "baseui/form-control";
import NodeLogo from "../NodeLogo";
import { FileType, InputNodeDescriptor } from "../../Api/Api";
import { toggleIsExpanded, TreeNodeData, TreeView } from "baseui/tree-view";
import { NodeFileDataModal } from "../../Shared/NodeFileDataModal";
import { useTransformEngineStyles } from "./TransformEngineStyles";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { ThemeType } from "../../DesignSystem/Theme/Theme";
import { OverflowActions, OverflowMenu } from "../../Shared/OverflowMenu";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";

export interface HandleRefTuple {
  inputHandle: HTMLDivElement | null;
  inputPosition: Position | null;
  outputHandle: HTMLDivElement | null;
  outputPosition: Position | null;
}

export interface TransformEngineNodeEntryProps {
  entry: TransformEngineNodeEntry;
}

export interface TransformEngineNodeProps {
  data: TransformEngineNodeData;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  subtitle?: React.ReactNode;
  mainComponent?: React.ReactNode;
}

export function TransformEngineInputNodeComponent(
  props: TransformEngineNodeProps
): JSX.Element {
  const {
    BASE_TRANSFORM_ENGINE_NODE_STYLE,
    FILE_BUTTON_STYLE,
    FILE_ICON_STYLE,
    NODE_HEADER_TEXT_STYLE,
    INPUT_NODE_SEARCH_INPUT_OVERRIDES,
    SELECTED_NODE_BORDER_STYLE,
  } = useTransformEngineStyles();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isNodeFileDataModalOpen, setIsNodeFileDataModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(true);
  const [searchValue, setSearchValue] = React.useState("");
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [expandButtonClicked, setExpandButtonClickedc] = React.useState<{
    expand: boolean;
  } | null>(null);

  const data = props.data;
  const { projectState, projectDispatch } = useContext(ProjectContext);

  const [isDraggingHandle, setIsDraggingHandle] = useState(false);

  const { currentTheme } = useDexterTheme();

  const { rfEdges, rfNodes } = projectState;
  const node = rfNodes.find((node) => node.id == data.dataID);
  const isSelected = node?.selected ?? false;
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const editContainerRef = useRef<HTMLDivElement | null>(null);
  const titleEditInProgressRef = useRef(data.titleEditInProgress);
  titleEditInProgressRef.current = data.titleEditInProgress;

  useEffect(() => {
    if (!isNodeFileDataModalOpen) return;

    const blockDeleteKeys = (event: KeyboardEvent): void => {
      if (event.key === "Delete" || event.key === "Backspace") {
        event.stopPropagation();
      }
    };

    window.addEventListener("keydown", blockDeleteKeys, true);
    return () => window.removeEventListener("keydown", blockDeleteKeys, true);
  }, [isNodeFileDataModalOpen]);

  useEffect(() => {
    if (data.titleEditInProgress) {
      const timeoutId = window.setTimeout(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }
  }, [data.titleEditInProgress]);

  const handleOpenFile = (): void => {
    setIsNodeFileDataModalOpen(true);
  };

  const startTitleEditing = useCallback(
    (nodeId: string) => {
      projectDispatch(["startTitleEditing", { nodeId }]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateTitleEdit = useCallback(
    (nodeId: string, wipTitle: string) => {
      projectDispatch(["updateTitleEdit", { nodeId, wipTitle }]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const confirmTitleEdit = useCallback(
    (nodeId: string) => {
      projectDispatch(["confirmTitleEdit", { nodeId }]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const discardTitleEdit = useCallback(
    (nodeId: string) => {
      projectDispatch(["discardTitleEdit", { nodeId }]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Explicit "click away" detection, independent of blur/focus timing.Fixes non-focus issue when clicking update title from overflow menu
  useEffect(() => {
    if (!data.titleEditInProgress) return;

    const handlePointerDown = (e: PointerEvent): void => {
      if (
        editContainerRef.current &&
        !editContainerRef.current.contains(e.target as Node)
      ) {
        // Flip this before dispatching so a blur firing in the same tick
        // doesn't try to re-focus an input we're about to tear down.
        titleEditInProgressRef.current = false;
        discardTitleEdit(data.dataID);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [data.titleEditInProgress, discardTitleEdit, data.dataID]);

  const endTitleEditing = useCallback(
    (action: "confirm" | "discard") => {
      titleEditInProgressRef.current = false;
      if (action === "confirm") {
        confirmTitleEdit(data.dataID);
      } else {
        discardTitleEdit(data.dataID);
      }
    },
    [confirmTitleEdit, discardTitleEdit, data.dataID]
  );

  function handleDelete(): void {
    if (nodeHasEdge(rfEdges, data.dataID)) {
      setIsDeleteConfirmationOpen(true);
    } else {
      const nodeChanges: NodeChange[] = [
        {
          id: data.dataID,
          type: "remove",
        },
      ];
      projectDispatch([
        "changeNodes",
        { changes: nodeChanges },
        projectDispatch,
      ]);
    }
  }

  function handleExpand(): void {
    setExpandButtonClickedc({ expand: !isExpanded });
  }
  const showValidationError =
    (data.titleInProgress ?? "").length > 0 &&
    !isNodeTitleValid(data.titleInProgress ?? "");

  const overflowActions: OverflowActions[] = [
    {
      label: "update title",
      onClick: () => {
        startTitleEditing(data.dataID);
      },
    },
    { label: "remove node", onClick: () => handleDelete() },
    {
      label: isSearchOpen ? "hide search" : "show search",
      onClick: () =>
        setIsSearchOpen((prev) => {
          if (prev) setSearchValue("");
          return !prev;
        }),
    },
    {
      label: !isExpanded ? "expand fields" : "collapse fields",
      onClick: () => handleExpand(),
    },
  ];

  // create one node with as many entries as it needs
  return (
    <div
      data-testid={`${data.title}-input-node`}
      style={{
        ...BASE_TRANSFORM_ENGINE_NODE_STYLE,
        minWidth: "200px",
        ...(isSelected ? SELECTED_NODE_BORDER_STYLE : {}),
      }}
      tabIndex={0}
    >
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        header={"Delete Node"}
        bodyText={`Are you sure you want to delete this node? It currently has at least one edge attached to it.`}
        onDelete={() => {
          const nodeChanges: NodeChange[] = [
            {
              id: data.dataID,
              type: "remove",
            },
          ];
          projectDispatch([
            "changeNodes",
            { changes: nodeChanges },
            projectDispatch,
          ]);
          setIsDeleteConfirmationOpen(false);
        }}
        onCancel={() => setIsDeleteConfirmationOpen(false)}
      />
      <NodeFileDataModal
        isOpen={
          "associatedFileId" in data.descriptor && isNodeFileDataModalOpen
        }
        header={data.descriptor.displayName ?? ""}
        fileType={
          (data.descriptor as InputNodeDescriptor).type ?? FileType.UNDETECTED
        }
        fileId={
          "storedFileId" in data.descriptor
            ? data.descriptor.storedFileId
            : undefined
        }
        onClose={() => setIsNodeFileDataModalOpen(false)}
      />

      {props.header}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
          minHeight: 34,
        }}
      >
        <>
          {/* Data Logo */}
          <div style={{ marginRight: 8 }}>
            <NodeLogo
              fileTypeFromDescriptor={
                (data.descriptor as InputNodeDescriptor).type
              }
            />
          </div>
          {/* Title */}
          <div
            style={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              gap: 2, // Tighter spacing between the title and edit button
            }}
          >
            {data.title && !data.titleEditInProgress ? (
              <>
                <StatefulTooltip
                  content={!node?.dragging && data.title.toLocaleLowerCase()}
                  placement={PLACEMENT.top}
                  showArrow
                  overrides={getTooltipOverrides(currentTheme)}
                >
                  <span
                    style={{ ...NODE_HEADER_TEXT_STYLE, fontSize: "1.3rem" }}
                  >
                    {data.title.toLocaleLowerCase().length > 10
                      ? `${data.title.toLocaleLowerCase().slice(0, 10)}...`
                      : data.title.toLocaleLowerCase()}
                  </span>
                </StatefulTooltip>
              </>
            ) : (
              <div
                ref={editContainerRef}
                className="nodrag"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "5px",
                  marginBottom: "-10px",
                }}
              >
                <FormControl
                  error={
                    showValidationError && "Title must be a valid file name"
                  }
                >
                  <Input
                    inputRef={titleInputRef}
                    overrides={{
                      ...INPUT_NODE_SEARCH_INPUT_OVERRIDES,
                      Root: {
                        style: {
                          ...(INPUT_NODE_SEARCH_INPUT_OVERRIDES as any)?.Root
                            ?.style,
                          width: "100%",
                        },
                      },
                    }}
                    value={data.titleInProgress ?? ""}
                    type="text"
                    size="mini"
                    onChange={(e) =>
                      updateTitleEdit(data.dataID, e.target.value)
                    }
                    onFocus={(e) => e.target.select()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (data.titleInProgress.trim().length == 0) {
                          endTitleEditing("discard");
                        } else if (showValidationError) {
                          // do nothing
                        } else {
                          endTitleEditing("confirm");
                        }
                      } else if (e.key === "Escape") {
                        endTitleEditing("discard");
                      }
                      e.stopPropagation();
                    }}
                    onBlur={() => {
                      // Still "in progress" means edit didn't end legitimately
                      // (that goes through endTitleEditing, which flips the ref).
                      // So the overflow menu took focus.
                      // Reclaim it for the input field.
                      if (titleEditInProgressRef.current) {
                        requestAnimationFrame(() =>
                          titleInputRef.current?.focus()
                        );
                      }
                    }}
                  />
                </FormControl>
              </div>
            )}
          </div>

          {/* Overflow menu with input node actions */}
          <StatefulTooltip
            content={"Action Menu"}
            placement={PLACEMENT.top}
            showArrow
            overrides={getTooltipOverrides(currentTheme)}
          >
            <div
              data-testId="node-overflow-menu"
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: 4,
              }}
            >
              <OverflowMenu overflowActions={overflowActions} />
            </div>
          </StatefulTooltip>
          {/* Raw Data Button */}
          <Button
            size="mini"
            style={{
              ...FILE_BUTTON_STYLE,
              marginLeft: "auto", // push to the far right
            }}
            onClick={handleOpenFile}
          >
            <BiFile style={FILE_ICON_STYLE} size={19} />
          </Button>
        </>
      </div>

      {/* Standalone parent hanlde to ensure it appears above the search field */}
      <ParentTreeHanlde setIsDraggingHandle={setIsDraggingHandle} />
      {isSearchOpen && (
        <div className="nodrag" style={{ width: "90%", marginTop: 6 }}>
          <div data-testid="node-search-input-field">
            <Input
              clearable
              clearOnEscape={false}
              aria-label="Find"
              placeholder="Find..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="mini"
              overrides={INPUT_NODE_SEARCH_INPUT_OVERRIDES}
            />
          </div>
        </div>
      )}

      {/* Added sppacing between search field and node tree data */}
      <div style={{ marginTop: "10px" }}>
        <NodeTreeView
          nodeData={data}
          setIsDraggingHandle={setIsDraggingHandle}
          isDraggingHandle={isDraggingHandle}
          rfEdges={rfEdges}
          currentTheme={currentTheme}
          searchValue={searchValue}
          expandButton={expandButtonClicked}
          onExpandStateChange={setIsExpanded}
        />
      </div>
    </div>
  );
}

function filterTreeBySearch(
  nodes: TreeNodeData[],
  query: string
): TreeNodeData[] {
  const lowerQuery = query.toLowerCase();

  function filterNodes(nodeList: TreeNodeData[]): TreeNodeData[] {
    const result: TreeNodeData[] = [];

    nodeList.forEach((node) => {
      const textMatch = (node.labelText ?? "")
        .toLowerCase()
        .includes(lowerQuery);

      if (textMatch) {
        // Name match: keep the entire subtree as-is (don't prune
        // children), just make sure this node is expanded so the
        // children are actually visible.
        result.push(node.isExpanded ? node : { ...node, isExpanded: true });
        return;
      }

      const hasChildren = node.children && node.children.length > 0;
      const filteredChildren = hasChildren ? filterNodes(node.children) : [];

      // Drop this branch entirely if no descendant matched either
      if (filteredChildren.length === 0) {
        return;
      }

      const originalChildren = node.children ?? [];
      const childrenChanged =
        filteredChildren.length !== originalChildren.length ||
        filteredChildren.some((child, i) => child !== originalChildren[i]);

      const needsExpansion = !node.isExpanded;

      if (!childrenChanged && !needsExpansion) {
        // Nothing to change here - keep the original reference.
        result.push(node);
      } else {
        result.push({
          ...node,
          children: filteredChildren,
          isExpanded: true,
        });
      }
    });

    return result;
  }

  return filterNodes(nodes);
}

function areAllNodesExpanded(nodes: TreeNodeData[]): boolean {
  return nodes.every((node) =>
    !node.children?.length
      ? true
      : node.isExpanded && areAllNodesExpanded(node.children)
  );
}

function setExpandedRecursively(
  nodes: TreeNodeData[],
  expanded: boolean
): TreeNodeData[] {
  return nodes.map((node) => ({
    ...node,
    isExpanded: expanded,
    children:
      node.children && node.children.length > 0
        ? setExpandedRecursively(node.children, expanded)
        : node.children,
  }));
}

function NodeTreeView({
  nodeData,
  setIsDraggingHandle,
  isDraggingHandle,
  rfEdges,
  currentTheme,
  searchValue,
  expandButton,
  onExpandStateChange,
}: {
  nodeData: TransformEngineNodeData;
  setIsDraggingHandle: (dragging: boolean) => void;
  isDraggingHandle: boolean;
  rfEdges: ReactFlowEdge[];
  currentTheme: ThemeType;
  searchValue: string;
  expandButton: { expand: boolean } | null;
  onExpandStateChange: (allExpanded: boolean) => void;
}): JSX.Element {
  const entries: TransformEngineNodeEntry[] = nodeData.entries;

  useEffect(() => {
    if (!expandButton) return;
    setTreeData((prevData) =>
      setExpandedRecursively(prevData, expandButton.expand)
    );
  }, [expandButton]);

  const [hoveredEntryId, setHoveredEntryId] =
    React.useState<TransformEngineNodeEntryId | null>(null);
  const [draggedEntryId, setDraggedEntryId] =
    React.useState<TransformEngineNodeEntryId | null>(null);

  // Once the drag ends, remove the entry highlight
  React.useEffect(() => {
    if (!isDraggingHandle) {
      setDraggedEntryId(null);
    }
  }, [isDraggingHandle]);

  const syntaxHighlightedText = React.useCallback(
    (text: string): JSX.Element => {
      const fileType = (nodeData.descriptor as InputNodeDescriptor).type;

      const code = fileType === FileType.XML ? `<${text}>` : text;

      return <NodeSyntaxHighlighter code={code} fileType={fileType} />;
    },
    [nodeData.descriptor]
  );

  // Build initial tree structure only once
  const buildInitialTree = React.useMemo(() => {
    // First pass: build the tree structure
    const rootNodes: TreeNodeData[] = [];

    entries.forEach(
      (entry) => {
        let currentLevel = rootNodes;

        entry.path.pathEntries.forEach((pathEntry, pathEntryIndex) => {
          const label = pathEntry.byString;
          const isLeaf = pathEntryIndex === entry.path.pathEntries.length - 1;

          let existingNode = currentLevel.find(
            (node) => node.labelText === label
          );
          if (!existingNode) {
            existingNode = {
              id: entry.id,
              labelText: label,
              label: () => syntaxHighlightedText(label),
              isExpanded: false,
              children: [],
            };
            currentLevel.push(existingNode);
          }

          if (!isLeaf) {
            currentLevel = existingNode.children;
          }
        });
      },
      [entries, nodeData, rfEdges, syntaxHighlightedText]
    );

    // Second pass: determine which nodes should be expanded based on edges
    const nodesToExpand = new Set<string>();

    entries.forEach((entry) => {
      const handle = findNodeHandleForEntry(nodeData, entry.id);

      if (singleHandleHasEdge(rfEdges, nodeData.dataID, handle.id)) {
        // Get all path segments for this entry (including ancestors for expansion)
        const accumulatedParentNodes: string[] = [];
        entry.path.pathEntries.forEach((pathEntry) => {
          accumulatedParentNodes.push(pathEntry.byString);
          nodesToExpand.add(accumulatedParentNodes.join("/"));
        });
      }
    });

    const applyExpansionState = (
      nodes: TreeNodeData[],
      currentPath: string[] = []
    ): void => {
      nodes.forEach((node) => {
        const labelPath = [...currentPath, node.labelText];
        const fullPath = labelPath.join("/");
        if (nodesToExpand.has(fullPath)) {
          node.isExpanded = true;
        }
        if (node.children.length > 0) {
          applyExpansionState(node.children, labelPath);
        }
      });
    };

    applyExpansionState(rootNodes);

    return rootNodes;
  }, [entries, nodeData, rfEdges, syntaxHighlightedText]);

  const [treeData, setTreeData] = React.useState(buildInitialTree);

  useEffect(() => {
    onExpandStateChange(areAllNodesExpanded(treeData));
  }, [treeData, onExpandStateChange]);

  // When searching, derive a filtered view containing only nodes that
  // match the query (plus ancestors needed to reach them). Matching
  // branches are force-expanded so results are visible immediately.
  const trimmedSearch = searchValue.trim();

  const displayedTreeData = React.useMemo(() => {
    if (!trimmedSearch) {
      return treeData;
    }
    return filterTreeBySearch(treeData, trimmedSearch);
  }, [treeData, trimmedSearch]);

  // Highlight during a drag as well as hovering over the entry's handle
  const highlightedEntryId = isDraggingHandle ? draggedEntryId : hoveredEntryId;

  return (
    <div style={{ display: "flex", position: "relative", width: "100%" }}>
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        {trimmedSearch && displayedTreeData.length === 0 ? (
          <div
            style={{
              padding: "8px 4px",
              color: currentTheme.colors.errorTextColor,
              fontSize: currentTheme.fontSizes.small,
              font: currentTheme.fonts.primary,
            }}
          >
            No match found for {`"${trimmedSearch}"`}
          </div>
        ) : (
          <TreeView
            data={displayedTreeData}
            onToggle={(node) =>
              setTreeData((prevData) => toggleIsExpanded(prevData, node))
            }
            overrides={{
              TreeLabel: {
                style: {
                  paddingRight: "15px",
                  color: currentTheme.colors.text,
                  ":hover": {
                    backgroundColor: currentTheme.colors.pageBackgroundColor,
                  },
                },
              },
            }}
          />
        )}
      </div>
      <TreeViewHandles
        entries={entries}
        treeData={displayedTreeData}
        data={nodeData}
        setIsDraggingHandle={setIsDraggingHandle}
        highlightedEntryId={highlightedEntryId}
        setHoveredEntryId={setHoveredEntryId}
        setDraggedEntryId={setDraggedEntryId}
        currentTheme={currentTheme}
      />
    </div>
  );
}

function TreeViewHandles({
  entries,
  treeData,
  data,
  setIsDraggingHandle,
  highlightedEntryId,
  setHoveredEntryId,
  setDraggedEntryId,
  currentTheme,
}: {
  entries: TransformEngineNodeEntry[];
  treeData: TreeNodeData[];
  data: TransformEngineNodeData;
  setIsDraggingHandle: (dragging: boolean) => void;
  highlightedEntryId: TransformEngineNodeEntryId | null;
  setHoveredEntryId: React.Dispatch<
    React.SetStateAction<TransformEngineNodeEntryId | null>
  >;
  setDraggedEntryId: React.Dispatch<
    React.SetStateAction<TransformEngineNodeEntryId | null>
  >;
  currentTheme: ThemeType;
}): JSX.Element {
  const handleVerticalOffset = 2.5;
  const pixelSpaceBetweenEntries = 32.8;

  const isEnumValue = (entry: TransformEngineNodeEntry): boolean => {
    const pathEntries = entry.path.pathEntries;
    if (pathEntries.length === 0) return false;

    const label = pathEntries[pathEntries.length - 1].byString;
    // Enum values match pattern: "NAME = NUMBER" (e.g., "ELECTRONICS = 0")
    return /^[A-Z_]+\s*=\s*\d+$/.test(label);
  };

  const isOneOfChild = (entry: TransformEngineNodeEntry): boolean => {
    const pathEntries = entry.path.pathEntries;
    if (pathEntries.length < 2) return false;
    // Check if the parent entry (second-to-last) is a oneof structure
    const parentLabel = pathEntries[pathEntries.length - 2].byString;
    return parentLabel?.includes("oneof ") ?? false;
  };

  const isServiceChild = (entry: TransformEngineNodeEntry): boolean => {
    const pathEntries = entry.path.pathEntries;
    if (pathEntries.length < 2) return false;
    const parentLabel = pathEntries[pathEntries.length - 2].byString;
    return parentLabel?.startsWith("service ") ?? false;
  };

  // Flatten tree and collect visible paths (including children)
  const flattenTree = (
    nodes: TreeNodeData[],
    path: string[] = []
  ): TreeNodeData[] => {
    return nodes.reduce((result, node) => {
      result.push({ ...node, path: [...path, node.labelText] });
      if (node.isExpanded && node.children?.length > 0) {
        result = result.concat(
          flattenTree(node.children, [...path, node.labelText])
        );
      }
      return result;
    }, []);
  };

  const visibleTreeNodes = flattenTree(treeData);

  // Find entries that match visible tree nodes by path
  const visibleEntries: TransformEngineNodeEntry[] = [];

  visibleTreeNodes.forEach((treeNode) => {
    const visibleEntry = entries.find((entry) => {
      const entryPath = entry.path.pathEntries.map((entry) => entry.byString);
      const treePath = treeNode.path;
      return (
        entryPath.length === treePath.length &&
        entryPath.every((path, pathIndex) => path === treePath[pathIndex])
      );
    });

    if (visibleEntry) {
      visibleEntries.push(visibleEntry);
    }
  });

  // Find the entry associated with the currently highlighted handle
  const highlightedIndex = visibleEntries.findIndex(
    (entry) => entry.id === highlightedEntryId
  );

  // Path segments for each visible entry, in the same order as visibleEntries,
  // used to detect which rows are descendants of the highlighted entry.
  const visibleEntryPaths: string[][] = visibleEntries.map((entry) =>
    entry.path.pathEntries.map((pathEntry) => pathEntry.byString)
  );

  // If the highlighted entry is an expanded parent, extend the highlight
  // to cover all of its currently-visible children
  let highlightedEndIndex = highlightedIndex;
  if (highlightedIndex !== -1) {
    const parentPath = visibleEntryPaths[highlightedIndex];
    for (let i = highlightedIndex + 1; i < visibleEntryPaths.length; i++) {
      const candidatePath = visibleEntryPaths[i];
      const isDescendant =
        candidatePath.length > parentPath.length &&
        parentPath.every(
          (segment, segmentIndex) => candidatePath[segmentIndex] === segment
        );

      if (!isDescendant) {
        break;
      }
      highlightedEndIndex = i;
    }
  }

  const highlightTopOffset = 2;

  return (
    <>
      {/* Highlight bar for the currently hovered/dragged entry and visible children */}
      {highlightedIndex !== -1 && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${
              highlightedIndex * pixelSpaceBetweenEntries - highlightTopOffset
            }px`,
            height:
              (highlightedEndIndex - highlightedIndex + 1) *
              pixelSpaceBetweenEntries,
            backgroundColor: currentTheme.colors.entryHighlightColor,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Entry handles (for all visible entries, including children) */}
      {visibleEntries.map((entry, index) => {
        const refTuple: HandleRefTuple = {
          inputHandle: null,
          inputPosition: null,
          outputHandle: null,
          outputPosition: null,
        };

        const handle = findNodeHandleForEntry(data, entry.id);
        const verticalPosition =
          index * pixelSpaceBetweenEntries + handleVerticalOffset;

        // Check if this is an enum value that should be disabled
        const shouldDisable =
          isEnumValue(entry) || isOneOfChild(entry) || isServiceChild(entry);

        return (
          <div
            key={`handle-hover-${entry.id}`}
            onMouseEnter={() => setHoveredEntryId(entry.id)}
            onMouseLeave={() =>
              setHoveredEntryId((prev) => (prev === entry.id ? null : prev))
            }
            onMouseDown={() => setDraggedEntryId(entry.id)}
          >
            {!shouldDisable &&
              createHandleComponentsForHandle(
                handle,
                entry.path.pathType,
                refTuple,
                verticalPosition,
                setIsDraggingHandle,
                shouldDisable // Pass the disabled flag for enum values (or any other)
              )}
          </div>
        );
      })}
    </>
  );
}

function ParentTreeHanlde({
  setIsDraggingHandle,
}: {
  setIsDraggingHandle: (dragging: boolean) => void;
}): JSX.Element {
  const { NODE_PARENT_HANDLE_STYLE } = useTransformEngineStyles();

  return (
    <Handle
      key={PARENT_HANDLE_ID}
      type="source"
      position={Position.Right}
      id={PARENT_HANDLE_ID}
      style={NODE_PARENT_HANDLE_STYLE}
      className={EXPANDED_HANDLE_CLASS}
      onMouseDown={() => {
        setIsDraggingHandle(true);
        const stopDragging = (): void => {
          setIsDraggingHandle(false);
          document.removeEventListener("mouseup", stopDragging);
        };
        document.addEventListener("mouseup", stopDragging);
      }}
    />
  );
}
